// ── The local mirror of an email being composed (owner incident 2026-09-23) ──────────────────────
// useAutoSaveDraft mirrors every composer's snapshot into localStorage as a crash/offline safety
// net; the SERVER Drafts folder (api.saveDraft) is the real store. The mirror used to be unbounded
// and never swept: one browser held 259 mirrors / 10.2 MB — the whole origin quota — with reply
// threads of ~1 MB each dating back three months. From then on EVERY localStorage.setItem in the
// app threw QuotaExceededError, silently: the company switcher, the UI language, page size… none
// of them "stuck" any more. Nothing in the app reads a mirror back today, so keeping them costs
// everything and buys nothing.
//
// Rules, all enforced here and only here:
//   • a mirror older than TTL_MS is dead — the server copy is months newer;
//   • all mirrors together stay under BUDGET_BYTES, oldest evicted first;
//   • a single snapshot over ITEM_CAP_BYTES (a quoted thread) is not mirrored — the server has it;
//   • a quota error evicts every mirror and retries once, so the app's other settings keep working.

const PREFIX = 'mf_draft_';
const TTL_MS = 7 * 24 * 3600 * 1000;      // a week: anything older lives in the server Drafts folder
const BUDGET_BYTES = 1.5 * 1024 * 1024;   // ≈15% of the 10 MB origin quota
const ITEM_CAP_BYTES = 256 * 1024;

export const isDraftMirrorKey = (k: string) => k.startsWith(PREFIX);

type Entry = { key: string; bytes: number; ts: number };

function entries(): Entry[] {
  const out: Entry[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || '';
      if (!key.startsWith(PREFIX)) continue;
      const v = localStorage.getItem(key) || '';
      let ts = 0;
      try { ts = Number(JSON.parse(v)?.ts || 0) || 0; } catch { /* unreadable → oldest */ }
      out.push({ key, bytes: (key.length + v.length) * 2, ts });
    }
  } catch { /* storage unavailable */ }
  return out.sort((a, b) => a.ts - b.ts); // oldest first
}

/** Evict expired mirrors, then the oldest until the rest fit the budget. Returns bytes freed. */
export function sweepDraftMirrors(now = Date.now()): number {
  let freed = 0;
  const live: Entry[] = [];
  for (const e of entries()) {
    if (!e.ts || now - e.ts > TTL_MS) { try { localStorage.removeItem(e.key); freed += e.bytes; } catch { /* ignore */ } }
    else live.push(e);
  }
  let total = live.reduce((s, e) => s + e.bytes, 0);
  for (const e of live) {
    if (total <= BUDGET_BYTES) break;
    try { localStorage.removeItem(e.key); freed += e.bytes; total -= e.bytes; } catch { /* ignore */ }
  }
  return freed;
}

/** Drop every mirror — the last resort when the origin's quota is exhausted by something else. */
export function dropAllDraftMirrors(): number {
  let freed = 0;
  for (const e of entries()) { try { localStorage.removeItem(e.key); freed += e.bytes; } catch { /* ignore */ } }
  return freed;
}

/** Mirror one snapshot. Oversized snapshots are skipped (and any stale mirror under that key
 *  removed, so a shrunken draft never leaves a fat ghost). Never throws. */
let writes = 0;
export function writeDraftMirror(key: string, value: string): void {
  try {
    if (value.length * 2 > ITEM_CAP_BYTES) { localStorage.removeItem(key); return; }
    // The budget is re-checked every 20th write, not only at boot — a long session with several
    // composers open must not regrow the mirrors back to the quota between reloads.
    if (++writes % 20 === 0) sweepDraftMirrors();
    try { localStorage.setItem(key, value); }
    catch {
      sweepDraftMirrors();
      try { localStorage.setItem(key, value); } catch { /* still full: the server copy stands */ }
    }
  } catch { /* storage unavailable */ }
}

export function removeDraftMirror(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** Retry one storage write after making room: sweep first, then drop all mirrors. Used by the
 *  app's small settings (active company, UI language…) so a full quota never eats a preference.
 *  Returns whether the write finally landed. */
export function setItemWithRoom(key: string, value: string): boolean {
  const attempt = () => { try { localStorage.setItem(key, value); return true; } catch { return false; } };
  if (attempt()) return true;
  sweepDraftMirrors();
  if (attempt()) return true;
  dropAllDraftMirrors();
  return attempt();
}

// Sweep as soon as this module loads — it is imported by utils/api.ts, i.e. by EVERY page (staff
// shell, login screen, client portal), so no boot path writes into a full origin.
try { sweepDraftMirrors(); } catch { /* storage unavailable */ }
